/* <td-booster-buybox-builder> — each checked booster set resolves to ONE pre-made
   product (combo JSON); its variant (+ Recharge plan on sub) drives the <product-form>. */
if (!customElements.get('td-booster-buybox-builder')) {
  const SELECTORS = {
    atcButton: '[data-td-bbb-atc-button]',
    atcComparePrice: '[data-atc-compare-price]',
    atcPrice: '[data-atc-price]',
    autoPrice: '[data-td-bbb-auto-price]',
    basePrice: '[data-td-bbb-base-price]',
    card: '[data-td-bbb-card]',
    cardShell: '[data-td-bbb-card-shell]',
    checkbox: '[data-td-bbb-checkbox]',
    combos: '[data-td-bbb-combos]',
    form: 'form[data-type="add-to-cart-form"]',
    mainSwiper: '[data-swiper]:not([data-swiper-thumbs])',
    onetimeNow: '[data-td-bbb-onetime-now]',
    onetimePlan: '[data-td-bbb-plan="onetime"]',
    onetimeWas: '[data-td-bbb-onetime-was]',
    planSave: '[data-td-bbb-plan-save]',
    previewBoosters: '[data-td-bbb-preview-boosters]',
    sellingPlanInput: 'input[name="selling_plan"]',
    soldOutLabel: '[data-td-bbb-soldout-label]',
    stack: '[data-td-bbb-stack]',
    stackBadge: '[data-td-bbb-stack-badge]',
    stackCount: '[data-td-bbb-stack-count]',
    stackList: '[data-td-bbb-stack-list]',
    stackNow: '[data-td-bbb-stack-now]',
    stackPerMonth: '[data-td-bbb-stack-per-month]',
    stackToggle: '[data-td-bbb-stack-toggle]',
    stackToggleText: '[data-td-bbb-stack-toggle-text]',
    stackWas: '[data-td-bbb-stack-was]',
    subscriptionLabel: '[data-td-bbb-subscription-label]',
    subscriptionNow: '[data-td-bbb-subscription-now]',
    subscriptionPlan: '[data-td-bbb-plan="subscription"]',
    subscriptionWas: '[data-td-bbb-subscription-was]',
    variantInput: 'input[name="id"]',
  };

  /** Resolves booster selections to their configured product and purchase plan. */
  class TdBoosterBuyboxBuilder extends HTMLElement {
    /**
     * Connects controls and resolves the initial stack.
     * @returns {void}
     */
    connectedCallback() {
      this.combos = this.parseCombos();
      this.checkboxes = Array.from(this.querySelectorAll(SELECTORS.checkbox));
      this.form = this.querySelector(SELECTORS.form);
      this.idInput = this.form ? this.form.querySelector(SELECTORS.variantInput) : null;
      this.subRadio = this.querySelector(SELECTORS.subscriptionPlan);
      this.onetimeRadio = this.querySelector(SELECTORS.onetimePlan);
      this.subToggleLabel = this.querySelector(SELECTORS.subscriptionLabel);
      this.basePriceEl = this.querySelector(SELECTORS.basePrice);
      this.atcPriceEl = this.querySelector(SELECTORS.atcPrice);
      this.atcCompareEl = this.querySelector(SELECTORS.atcComparePrice);
      this.atcButton = this.querySelector(SELECTORS.atcButton);
      this.boosterContainer = this.querySelector(SELECTORS.previewBoosters);

      this.handleCheckboxChange = this.handleCheckboxChange.bind(this);
      this.handlePlanChange = this.onPlanChange.bind(this);
      this.handleStackToggle = this.toggleStack.bind(this);

      this.stackEl = this.querySelector(SELECTORS.stack);
      if (this.stackEl) {
        this.stackToggle = this.stackEl.querySelector(SELECTORS.stackToggle);
        this.stackCount = this.stackEl.querySelector(SELECTORS.stackCount);
        this.stackToggleText = this.stackEl.querySelector(SELECTORS.stackToggleText);
        this.stackWas = this.stackEl.querySelector(SELECTORS.stackWas);
        this.stackNow = this.stackEl.querySelector(SELECTORS.stackNow);
        this.stackPermo = this.stackEl.querySelector(SELECTORS.stackPerMonth);
        this.stackBadge = this.stackEl.querySelector(SELECTORS.stackBadge);
        this.stackList = this.stackEl.querySelector(SELECTORS.stackList);
        this.baseTitle = this.stackEl.getAttribute('data-td-bbb-base-title') || '';
        this.baseLabel = this.stackEl.getAttribute('data-td-bbb-base-label') || '';
        this.basePrice = this.stackEl.getAttribute('data-td-bbb-base-price-value') || '';
        this.stackShowText = this.stackEl.getAttribute('data-td-bbb-stack-show-text') || "What's included";
        this.stackHideText = this.stackEl.getAttribute('data-td-bbb-stack-hide-text') || 'Hide details';
        this.stackBadgeText = this.stackEl.getAttribute('data-td-bbb-stack-badge-text') || 'Subscribe & Save';
        this.stackDiscountLabel = this.stackEl.getAttribute('data-td-bbb-stack-discount-label') || 'Stack discount';
        if (this.stackToggle) this.stackToggle.addEventListener('click', this.handleStackToggle);
      }

      this.checkboxes.forEach((cb) => cb.addEventListener('change', this.handleCheckboxChange));
      if (this.subRadio) this.subRadio.addEventListener('change', this.handlePlanChange);
      if (this.onetimeRadio) this.onetimeRadio.addEventListener('change', this.handlePlanChange);

      this.resolve();
    }

    /**
     * Disconnects all component event listeners.
     * @returns {void}
     */
    disconnectedCallback() {
      this.checkboxes.forEach((cb) => cb.removeEventListener('change', this.handleCheckboxChange));
      if (this.subRadio) this.subRadio.removeEventListener('change', this.handlePlanChange);
      if (this.onetimeRadio) this.onetimeRadio.removeEventListener('change', this.handlePlanChange);
      if (this.stackToggle) this.stackToggle.removeEventListener('click', this.handleStackToggle);
    }

    /**
     * Resolves the stack after a booster changes.
     * @returns {void}
     */
    handleCheckboxChange() {
      this.resolve();
      this.goToFirstSlide();
    }

    /**
     * Parses the server-rendered combo data.
     * @returns {Array<Object>} Normalized combo records.
     */
    parseCombos() {
      const el = this.querySelector(SELECTORS.combos);
      let raw = [];
      if (el) {
        try { raw = JSON.parse(el.textContent.trim()); } catch { raw = []; }
      }
      return raw.map((c) => Object.assign({}, c, {
        keys: String(c.boosterKeys || '').split(',').map((s) => s.trim()).filter(Boolean).sort(),
      }));
    }

    /**
     * Gets the sorted keys for selected boosters.
     * @returns {Array<string>} Selected booster keys.
     */
    checkedKeys() {
      return this.checkboxes
        .filter((cb) => cb.checked)
        .map((cb) => cb.getAttribute('data-td-bbb-booster-key'))
        .sort();
    }

    /**
     * Tests whether two sorted key arrays contain the same values.
     * @param {Array<string>} a - First key array.
     * @param {Array<string>} b - Second key array.
     * @returns {boolean} Whether the arrays match.
     */
    sameSet(a, b) {
      return a.length === b.length && a.every((k, i) => k === b[i]);
    }

    /**
     * Finds the combo matching a set of booster keys.
     * @param {Array<string>} keys - Sorted booster keys.
     * @returns {Object|null} Matching combo, when configured.
     */
    findCombo(keys) {
      return this.combos.find((c) => this.sameSet(c.keys, keys)) || null;
    }

    /**
     * Checks whether subscription is selected.
     * @returns {boolean} Whether the subscription plan is active.
     */
    isSub() {
      return this.subRadio ? this.subRadio.checked : false;
    }

    /**
     * Refreshes prices and plan inputs after the plan changes.
     * @returns {void}
     */
    onPlanChange() {
      if (!this.currentCombo) return;
      this.applyPlan(this.currentCombo);
      this.updatePrice(this.currentCombo);
      this.updateBasePrice();
      this.updateCardPrices();
      this.updateStack(this.currentCombo);
    }

    /**
     * Resolves the selected stack and synchronizes all dependent UI.
     * @returns {void}
     */
    resolve() {
      const keys = this.checkedKeys();
      const combo = this.findCombo(keys);
      this.currentCombo = combo;

      if (combo && combo.available) {
        if (this.idInput) this.idInput.value = combo.variantId;
        this.applySubAvailability(combo);
        this.applyPlan(combo);
        this.updatePrice(combo);
        this.updatePlanPrices(combo);
        this.setAtcDisabled(false);
      } else {
        this.setAtcDisabled(true);
      }

      this.updateDisabledCheckboxes(keys);
      this.updateActiveStyles();
      this.updateGallery();
      this.updateBasePrice();
      this.updateCardPrices();
      this.updateStack(combo);
    }

    // No Recharge plan -> hide Subscription, force One-Time.
    /**
     * Applies selling-plan availability for a combo.
     * @param {Object} combo - Active combo record.
     * @returns {void}
     */
    applySubAvailability(combo) {
      if (this.subToggleLabel) this.subToggleLabel.style.display = combo.hasSub ? '' : 'none';
      if (!combo.hasSub && this.onetimeRadio && !this.onetimeRadio.checked) {
        this.onetimeRadio.checked = true;
      }
    }

    // selling_plan input present only for a subscription.
    /**
     * Synchronizes the Recharge selling-plan form input.
     * @param {Object} combo - Active combo record.
     * @returns {void}
     */
    applyPlan(combo) {
      if (!this.form) return;
      let sp = this.form.querySelector(SELECTORS.sellingPlanInput);
      const wantSub = this.isSub() && combo.hasSub && combo.sellingPlanId;
      if (wantSub) {
        if (!sp) {
          sp = document.createElement('input');
          sp.type = 'hidden';
          sp.name = 'selling_plan';
          this.form.appendChild(sp);
        }
        sp.value = combo.sellingPlanId;
      } else if (sp) {
        sp.remove();
      }
    }

    /**
     * Updates the active add-to-cart price.
     * @param {Object} combo - Active combo record.
     * @returns {void}
     */
    updatePrice(combo) {
      const sub = this.isSub() && combo.hasSub;
      const priceMoney = sub ? combo.subMoney : combo.regMoney;
      const priceCents = sub ? combo.subCents : combo.regCents;
      const compareMoney = sub ? combo.subCompareMoney : combo.compareMoney;
      const compareCents = sub ? combo.subCompareCents : combo.compareCents;
      if (this.atcPriceEl) this.atcPriceEl.textContent = priceMoney;
      if (this.atcCompareEl) this.atcCompareEl.textContent = compareCents > priceCents ? compareMoney : '';
    }

    /**
     * Updates subscription and one-time plan card prices.
     * @param {Object} combo - Active combo record.
     * @returns {void}
     */
    updatePlanPrices(combo) {
      /**
       * Sets text on a component element.
       * @param {string} sel - Element selector.
       * @param {string} val - Text value.
       * @returns {void}
       */
      const setText = (sel, val) => {
        const el = this.querySelector(sel);
        if (el) el.textContent = val;
      };
      /**
       * Updates a compare-price element.
       * @param {string} sel - Element selector.
       * @param {boolean} show - Whether the value is visible.
       * @param {string} val - Money value.
       * @returns {void}
       */
      const setStrike = (sel, show, val) => {
        const el = this.querySelector(sel);
        if (!el) return;
        el.textContent = show ? val : '';
        el.style.display = show ? '' : 'none';
      };

      const subDiscount = combo.hasSub && combo.regCents > combo.subCents;
      setText(SELECTORS.subscriptionNow, combo.subMoney);
      setStrike(SELECTORS.subscriptionWas, subDiscount, combo.regMoney);
      const save = this.querySelector(SELECTORS.planSave);
      if (save) {
        if (subDiscount) {
          const pct = Math.round(((combo.regCents - combo.subCents) / combo.regCents) * 100);
          save.textContent = 'Save ' + pct + '%';
          save.style.display = '';
        } else {
          save.textContent = '';
          save.style.display = 'none';
        }
      }

      setText(SELECTORS.onetimeNow, combo.regMoney);
      setStrike(SELECTORS.onetimeWas, combo.compareCents > combo.regCents, combo.compareMoney);
    }

    // Plan-aware base price so itemized rows sum to the totals.
    /**
     * Updates the base-product price for the active plan.
     * @returns {void}
     */
    updateBasePrice() {
      const base = this.findCombo([]);
      if (!base) return;
      const sub = this.isSub() && base.hasSub;
      const money = sub ? base.subMoney : base.regMoney;
      if (this.basePriceEl) this.basePriceEl.textContent = money;
      this.basePrice = money;
    }

    // Formats cents using a server-rendered money string as the template.
    /**
     * Formats cents using a server-rendered money value as the template.
     * @param {number} cents - Amount in cents.
     * @param {string} sample - Server-rendered money string.
     * @returns {string} Formatted money value.
     */
    formatMoney(cents, sample) {
      let num = (cents / 100).toFixed(2).replace(/\.00$/, '');
      return String(sample || '$0').replace(/[\d.,]+/, num);
    }

    // Auto card price (blank price_label) = plan-aware delta
    // (solo combo - base); syncs the booster-price hook for the summary rows.
    /**
     * Updates computed booster-card prices for the active plan.
     * @returns {void}
     */
    updateCardPrices() {
      const base = this.findCombo([]);
      if (!base) return;
      this.checkboxes.forEach((cb) => {
        const card = cb.closest(SELECTORS.card);
        const el = card ? card.querySelector(SELECTORS.autoPrice) : null;
        if (!el) return;
        const solo = this.findCombo([cb.getAttribute('data-td-bbb-booster-key')]);
        if (!solo) {
          el.textContent = '';
          cb.removeAttribute('data-td-bbb-booster-price');
          return;
        }
        const sub = this.isSub() && solo.hasSub && base.hasSub;
        const cents = sub ? solo.subCents - base.subCents : solo.regCents - base.regCents;
        if (cents <= 0) {
          el.textContent = '';
          cb.removeAttribute('data-td-bbb-booster-price');
          return;
        }
        const money = this.formatMoney(cents, base.regMoney);
        el.textContent = '+' + money;
        cb.setAttribute('data-td-bbb-booster-price', money);
      });
    }

    /**
     * Sets the add-to-cart availability state.
     * @param {boolean} disabled - Whether purchasing is disabled.
     * @returns {void}
     */
    setAtcDisabled(disabled) {
      if (!this.atcButton) return;
      this.atcButton.disabled = disabled;
      this.atcButton.classList.toggle('is-unavailable', disabled);
    }

    // Disable a booster when its target combo is missing or unavailable. A booster
    // is sold out when no in-stock combo contains it, independent of the current stack.
    /**
     * Updates checkbox availability for the current stack.
     * @param {Array<string>} keys - Currently selected booster keys.
     * @returns {void}
     */
    updateDisabledCheckboxes(keys) {
      this.checkboxes.forEach((cb) => {
        const k = cb.getAttribute('data-td-bbb-booster-key');
        const next = cb.checked ? keys.filter((x) => x !== k) : keys.concat(k).sort();
        const nextCombo = this.findCombo(next);
        const ok = !!(nextCombo && nextCombo.available);
        const soldOut = !this.combos.some((combo) => combo.available && combo.keys.includes(k));
        cb.disabled = !ok;
        const card = cb.closest(SELECTORS.card);
        const shell = cb.closest(SELECTORS.cardShell);
        const soldOutLabel = card ? card.querySelector(SELECTORS.soldOutLabel) : null;
        if (card) {
          card.classList.toggle('is-disabled', !ok);
          card.classList.toggle('is-sold-out', soldOut);
        }
        if (shell) shell.classList.toggle('is-sold-out', soldOut);
        if (soldOutLabel) soldOutLabel.setAttribute('aria-hidden', String(!soldOut));
      });
    }

    /**
     * Synchronizes visual selected states.
     * @returns {void}
     */
    updateActiveStyles() {
      this.checkboxes.forEach((cb) => {
        const card = cb.closest(SELECTORS.card);
        if (card) card.classList.toggle('td-active', cb.checked);
      });
    }

    /**
     * Returns the gallery to its generated stack slide.
     * @returns {void}
     */
    goToFirstSlide() {
      // Swiper requires its own data attributes, so this selector is the approved library exception.
      const main = this.querySelector(SELECTORS.mainSwiper);
      if (main && main.swiper) main.swiper.slideTo(0);
    }

    /**
     * Rebuilds the selected-booster gallery composite.
     * @returns {void}
     */
    updateGallery() {
      if (!this.boosterContainer) return;
      this.boosterContainer.textContent = '';
      this.checkboxes.filter((cb) => cb.checked).forEach((cb) => {
        const src = cb.getAttribute('data-td-bbb-booster-image');
        if (!src) return;
        const plus = document.createElement('span');
        plus.className = 'td-bbb__preview-plus';
        plus.setAttribute('aria-hidden', 'true');
        plus.textContent = '+';
        const img = document.createElement('img');
        img.className = 'td-bbb__preview-booster';
        img.src = src;
        img.alt = '';
        this.boosterContainer.appendChild(plus);
        this.boosterContainer.appendChild(img);
      });
    }

    /**
     * Toggles the stack itemization panel.
     * @returns {void}
     */
    toggleStack() {
      if (!this.stackToggle) return;
      const open = this.stackToggle.getAttribute('aria-expanded') === 'true';
      this.stackToggle.setAttribute('aria-expanded', open ? 'false' : 'true');
      if (this.stackList) this.stackList.hidden = open;
      if (this.stackToggleText) this.stackToggleText.textContent = open ? this.stackShowText : this.stackHideText;
    }

    /**
     * Updates stack totals and itemization.
     * @param {Object|null} combo - Active combo record.
     * @returns {void}
     */
    updateStack(combo) {
      if (!this.stackEl || !combo) return;
      const count = 1 + this.checkedKeys().length;
      if (this.stackCount) this.stackCount.textContent = count + (count === 1 ? ' item' : ' items');

      const sub = this.isSub() && combo.hasSub;
      const nowMoney = sub ? combo.subMoney : combo.regMoney;
      const nowCents = sub ? combo.subCents : combo.regCents;
      const wasMoney = sub ? combo.subCompareMoney : combo.compareMoney;
      const wasCents = sub ? combo.subCompareCents : combo.compareCents;

      if (this.stackNow) this.stackNow.textContent = nowMoney;
      if (this.stackWas) {
        const show = wasCents > nowCents;
        this.stackWas.textContent = show ? wasMoney : '';
        this.stackWas.style.display = show ? '' : 'none';
      }
      if (this.stackPermo) this.stackPermo.style.display = sub ? '' : 'none';

      let pct = 0;
      if (sub && wasCents > nowCents) pct = Math.round(((wasCents - nowCents) / wasCents) * 100);
      if (this.stackBadge) {
        if (sub && pct > 0) {
          this.stackBadge.textContent = this.stackBadgeText + ' ' + pct + '% applied';
          this.stackBadge.style.display = '';
        } else {
          this.stackBadge.textContent = '';
          this.stackBadge.style.display = 'none';
        }
      }
      this.renderStackList(combo, sub);
    }

    /**
     * Parses a formatted money value into cents.
     * @param {string} money - Formatted money value.
     * @returns {number} Parsed amount in cents.
     */
    parseCents(money) {
      return Math.round(parseFloat(String(money).replace(/[^0-9.]/g, '')) * 100) || 0;
    }

    /**
     * Rebuilds the expanded stack itemization.
     * @param {Object} combo - Active combo record.
     * @param {boolean} sub - Whether subscription pricing is active.
     * @returns {void}
     */
    renderStackList(combo, sub) {
      if (!this.stackList) return;
      this.stackList.textContent = '';
      /**
       * Appends one itemization row.
       * @param {string} name - Row name.
       * @param {string} label - Optional row label.
       * @param {string} labelColor - Optional label color.
       * @param {string} price - Formatted row price.
       * @param {boolean} isSave - Whether this is a discount row.
       * @returns {void}
       */
      const addRow = (name, label, labelColor, price, isSave) => {
        const r = document.createElement('div');
        r.className = 'td-bbb__stack-row' + (isSave ? ' td-bbb__stack-row--save' : '');
        const left = document.createElement('span');
        left.className = 'td-bbb__stack-row-left';
        const n = document.createElement('span');
        n.className = 'td-bbb__stack-row-name';
        n.textContent = name;
        left.appendChild(n);
        if (label) {
          const l = document.createElement('span');
          l.className = 'td-bbb__stack-row-label';
          if (labelColor) l.style.color = labelColor;
          l.textContent = label;
          left.appendChild(l);
        }
        const p = document.createElement('span');
        p.className = 'td-bbb__stack-row-price';
        p.textContent = price;
        r.appendChild(left);
        r.appendChild(p);
        this.stackList.appendChild(r);
      };

      addRow(this.baseTitle, this.baseLabel, '', this.basePrice, false);
      let rowSum = this.parseCents(this.basePrice);
      this.checkboxes.filter((cb) => cb.checked).forEach((cb) => {
        const name = cb.getAttribute('data-td-bbb-booster-name') || '';
        const cat = cb.getAttribute('data-td-bbb-booster-category') || '';
        const accent = cb.getAttribute('data-td-bbb-booster-accent') || '';
        const price = (cb.getAttribute('data-td-bbb-booster-price') || '').replace(/^\+\s*/, '');
        rowSum += this.parseCents(price);
        addRow(name, cat, accent, price, false);
      });
      // A multi-booster combo can be cheaper than its parts; the discount row
      // closes that gap so rows always sum to the total.
      if (combo) {
        const totalCents = sub ? combo.subCents : combo.regCents;
        const discount = rowSum - totalCents;
        if (discount > 0) {
          addRow(this.stackDiscountLabel, '', '', '−' + this.formatMoney(discount, combo.regMoney), true);
        }
      }
    }
  }
  customElements.define('td-booster-buybox-builder', TdBoosterBuyboxBuilder);
}
